import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, openDocument } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../components/AppShell';

/* ============================================================================
   THE 360 VIEW — tabs, each with its own date range.

   ⚠ WHY TABS RATHER THAN ONE CALL RETURNING EVERYTHING.

   The first version fetched all five sections on every open — eight queries whether
   or not anyone looked past the first tab.

   But the reason that mattered was not speed. It was that a DATE FILTER was
   impossible: sections came back baked into one response with fixed limits, so
   "last 60 days" was a constant nobody could change.

   A tab is a section you can ask for independently, which is exactly what a date
   range needs.
   ============================================================================ */

interface ProfileFact { label: string; value: string; }

/**
 * Something the reader can DO with a row.
 *
 * ⚠ Actions ride ALONGSIDE the rows, not inside them: actions[i] belongs to rows[i]. Rows stay
 * plain strings so one renderer serves all five tabs; a tab that wants buttons declares them
 * separately without changing the shape every other tab depends on.
 */
interface ProfileRowAction {
  label: string;
  /** ⚠ A server-supplied PATH. The client never assembles document URLs out of ids. */
  href: string;
}

interface ProfileSection {
  title: string;
  kind: string;
  columns: string[];
  rows: string[][];
  emptyMessage: string | null;
  summary: string | null;
  /** Null on almost every section — only Fees uses it today. */
  actions: ProfileRowAction[][] | null;
}

interface StudentHeader {
  studentId: string; fullName: string; admissionNumber: string;
  classLabel: string | null; status: string;
  guardianName: string | null; guardianMobile: string | null;
  headline: ProfileFact[];
}

interface StaffHeader {
  employeeId: string; fullName: string; employeeCode: string;
  designation: string | null; department: string | null;
  status: string; mobile: string | null;
  headline: ProfileFact[];
}

type Tab = { key: string; label: string; dated: boolean; roles?: readonly string[] };

const STUDENT_TABS: Tab[] = [
  // Background first: "who is this and where did they come from" is what someone opening a profile
  // for the first time actually wants, and it needs no date range to answer.
  { key: 'background',   label: 'Background',   dated: false },
  { key: 'attendance',   label: 'Attendance',   dated: true  },
  { key: 'results',      label: 'Results',      dated: false },
  /**
   * ⚠ THE ONLY TAB WITH A ROLE ON IT, and it needs one.
   *
   * This tab is every invoice, every payment, and the balance outstanding on a child. The whole
   * student list is behind "staff" — eleven roles — so before this line a StoreKeeper could open
   * any child and read the family's balance. The server now refuses (the endpoint moved to
   * "fees-position"); this line is so nobody is offered a tab that answers 403.
   *
   * ⚠ THE SERVER IS THE LOCK. This is the label on the door. Hiding a tab hides nothing from
   * anyone holding a token and a URL — which is the reason the endpoint was changed first.
   */
  { key: 'fees',         label: 'Fees',         dated: false, roles: ROLES.finance },
  { key: 'certificates', label: 'Certificates', dated: false },
  { key: 'remarks',      label: 'Remarks',      dated: true  },
];

const STAFF_TABS: Tab[] = [
  { key: 'attendance', label: 'Attendance', dated: true  },
  { key: 'leave',      label: 'Leave',      dated: true  },
  { key: 'history',    label: 'History',    dated: false },
];

/** Ranges a school actually thinks in. */
const RANGES = [
  { key: '30d',  label: '30 days',  days: 30  },
  { key: '90d',  label: '3 months', days: 90  },
  { key: '180d', label: 'This term', days: 180 },
  { key: '365d', label: 'This year', days: 365 },
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ProfilePage({ personId, kind, onClose }: {
  personId: string;
  kind: 'student' | 'staff';
  onClose: () => void;
}) {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const tabs = useMemo(
    () => (kind === 'student' ? STUDENT_TABS : STAFF_TABS).filter((t) => !t.roles || t.roles.includes(role)),
    [kind, role]);

  const [header, setHeader] = useState<StudentHeader | StaffHeader | null>(null);
  const [tab, setTab] = useState(tabs[0].key);
  const [range, setRange] = useState('30d');
  const [section, setSection] = useState<ProfileSection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⚠ Only render the actions column when a row actually has a button. A section with an empty
  // actions array (or none at all) must look exactly as it did before this change.
  const hasActions = !!section?.actions?.some((a) => a.length > 0);

  /**
   * Open a document a row points at.
   *
   * ⚠ The 403 case is called out separately. These are finance documents — a teacher opening a
   * student profile will not see the buttons at all, but a role that half-qualifies would
   * otherwise get a generic failure and no idea why.
   */
  const openAction = async (a: ProfileRowAction) => {
    try { await openDocument(a.href); }
    catch (e) {
      alert(e instanceof ApiError && e.status === 403
        ? 'Only the office can open fee documents.'
        : 'Could not open that document.');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await api.get<StudentHeader | StaffHeader>(`/api/profiles/${kind}/${personId}`);
        if (!cancelled) setHeader(h);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Could not load this profile.');
      }
    })();
    return () => { cancelled = true; };
  }, [personId, kind]);

  const loadTab = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = tabs.find((t) => t.key === tab);
      const days = RANGES.find((r) => r.key === range)?.days ?? 30;

      // The range is sent only for tabs that use one. A URL carrying a parameter the endpoint
      // ignores is a lie about what the screen is doing.
      const q = active?.dated ? `?from=${isoDaysAgo(days)}` : '';

      setSection(await api.get<ProfileSection>(`/api/profiles/${kind}/${personId}/${tab}${q}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load that section.');
      setSection(null);
    } finally {
      setLoading(false);
    }
  }, [kind, personId, tab, range, tabs]);

  useEffect(() => { void loadTab(); }, [loadTab]);

  if (error && !header) {
    return (
      <div className="sheet-backdrop" onClick={onClose}>
        <div className="sheet" onClick={(e) => e.stopPropagation()}>
          <p className="muted">{error}</p>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!header) {
    return (
      <div className="sheet-backdrop" onClick={onClose}>
        <div className="sheet"><p className="muted">Loading…</p></div>
      </div>
    );
  }

  const isStudent = 'studentId' in header;
  const subtitle = isStudent
    ? `${header.admissionNumber}${header.classLabel ? ` · ${header.classLabel}` : ''}`
    : `${(header as StaffHeader).employeeCode}${(header as StaffHeader).designation ? ` · ${(header as StaffHeader).designation}` : ''}`;
  const contact = isStudent ? header.guardianMobile : (header as StaffHeader).mobile;
  const activeTab = tabs.find((t) => t.key === tab);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet profile-sheet" onClick={(e) => e.stopPropagation()}>

        <div className="profile-head">
          <div>
            <h2 className="h-display" style={{ fontSize: 20, marginBottom: 2 }}>{header.fullName}</h2>
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>{subtitle}</p>
          </div>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <div className="profile-facts">
          {header.headline.map((f) => (
            <div key={f.label} className="profile-fact">
              <span className="profile-fact-value">{f.value}</span>
              <span className="profile-fact-label">{f.label}</span>
            </div>
          ))}
        </div>

        {contact && (
          <div className="profile-contact">
            <a href={`tel:${contact}`} className="btn btn-sm">Call {contact}</a>
          </div>
        )}

        {/* Tabs scroll sideways on a phone rather than wrapping onto two rows — a wrapped tab
            strip reads as a mistake. */}
        <div className="profile-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`profile-tab${t.key === tab ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* The range control appears only on tabs that use one. A date filter above a fee list that
            ignores it teaches the wrong thing about what the screen does. */}
        {activeTab?.dated && (
          <div className="profile-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`btn btn-sm${r.key === range ? ' is-active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="muted profile-empty">Loading…</p>}

        {!loading && error && <p className="profile-error">{error}</p>}

        {!loading && !error && section && (
          <>
            {/* The answer above the rows. A principal usually wants the number; the rows justify it. */}
            {section.summary && <p className="profile-summary">{section.summary}</p>}

            {section.rows.length === 0 ? (
              <p className="profile-empty">{section.emptyMessage ?? 'Nothing here yet.'}</p>
            ) : section.kind === 'facts' ? (
              /* A facts section is label/value pairs, not a table. Rendering it with DATE and STATUS
                 headers over "Guardian | Shetty" would be nonsense. */
              <dl className="profile-kv">
                {section.rows.map((row, i) => (
                  <div key={i}>
                    <dt>{row[0]}</dt>
                    <dd>{row[1] || '—'}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <div className="profile-table-wrap">
                <table className="profile-table">
                  <thead>
                    <tr>
                      {section.columns.map((c) => <th key={c}>{c}</th>)}
                      {/* ⚠ The actions column has no heading — a column called "Actions" above a
                          single button is noise on a narrow screen. */}
                      {hasActions && <th aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j} className={cell === 'Staff only' ? 'cell-flag' : undefined}>
                            {cell || '—'}
                          </td>
                        ))}
                        {hasActions && (
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {(section.actions?.[i] ?? []).map((a) => (
                              <button key={a.href} className="btn"
                                style={{ padding: '3px 9px', fontSize: 12, marginLeft: 6 }}
                                onClick={() => void openAction(a)}>
                                {a.label}
                              </button>
                            ))}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
